import React, {useState, ChangeEvent, forwardRef} from "react";

// Assets
import IconEye from "../../Assets/SVGs/Icons/UI/Eye";
import IconEyeOff from "../../Assets/SVGs/Icons/UI/EyeOff";

// Components
import ErrorMessage from "../Text/ErrorMessage";

interface InputFieldProps {
    type: string;
    name: string;
    value: string;
    label: string;
    onChange: (e: ChangeEvent<HTMLInputElement>) => void;
    onFocus?: (name: string) => void;
    onBlur?: (name: string) => void;
    hasError?: boolean;
    errorMessage?: string;
    required?: boolean;
    className?: string;
    disabled?: boolean;
    enablePasswordToggle?: boolean;
}

const InputField = forwardRef<HTMLInputElement, InputFieldProps>(
    (
        {
            type,
            name,
            value,
            label,
            onChange,
            onFocus,
            onBlur,
            hasError,
            errorMessage,
            required,
            className = "",
            disabled,
            enablePasswordToggle = true,
        },
        ref
    ) => {
        const
            [focusedInput, setFocusedInput] = useState<boolean>(false),
            [showPassword, setShowPassword] = useState<boolean>(false),

            isPasswordField = type === "password" && enablePasswordToggle,

            handleFocus = () => {
                setFocusedInput(true);
                onFocus && onFocus(name);
            },

            handleBlur = () => {
                setFocusedInput(false);
                onBlur && onBlur(name);
            },

            effectiveType =
                isPasswordField && showPassword ? "text" : type;

        return (
            <div
                className={`
                    form__input-wrapper form__input-text
                    ${hasError ? "has-error" : "has-no-error"}
                    ${focusedInput ? "is-focused" : "is-not-focused"}
                    ${value !== "" ? "has-value" : "has-no-value"}
                    ${className}
                `}
            >
                <div className="input-wrapper__cover">
                    {label && label !== "" && (
                        <label htmlFor={name}>{label}</label>
                    )}

                    <div
                        className={`input-wrapper__inner ${
                            isPasswordField ? "has-pw-toggle" : ""
                        }`}
                    >
                        <input
                            id={name}
                            type={effectiveType}
                            name={name}
                            ref={ref}
                            value={value}
                            onChange={onChange}
                            onFocus={handleFocus}
                            onBlur={handleBlur}
                            required={required}
                            disabled={disabled}
                            className={
                                !label || label === ""
                                    ? "has-no-label"
                                    : "has-label"
                            }
                        />

                        {isPasswordField && (
                            <button
                                type="button"
                                className="form__pw-show-toggle"
                                onClick={() => setShowPassword((v) => !v)}
                                aria-label={
                                    showPassword
                                        ? "Hide password"
                                        : "Show password"
                                }
                            >
                                {showPassword ? IconEyeOff() : IconEye()}
                            </button>
                        )}
                    </div>
                </div>

                {hasError && errorMessage && errorMessage !== "" && <ErrorMessage errorMessage={errorMessage} />}
            </div>
        );
    }
);

export default InputField;
